import { useEffect, useState } from "react";
import { AiGeneratedFlag, Badge, Button, Card, CardBody, CardHeader, CitationChip, ConfidenceBadge, ConfirmDialog, EmptyState, Field, StudioState, TextArea, TextInput } from "@odingard/design-system";
import type { MeetingCandidate } from "@odingard/sdk-client";
import { ApiError } from "@odingard/sdk-client";
import { meetingApi, api } from "../../lib/api";
import { Refusal } from "./MeetingActions";
import { meetingLabel } from "./MeetingShared";

export function CandidateReview({ orgId, meetingId, candidates, onChanged, permissions: _permissions = [], extractionOutcome }: { orgId: string; meetingId: string; candidates: MeetingCandidate[]; onChanged: () => void; permissions?: string[]; extractionOutcome?: string | undefined }) {
  const [selected, setSelected] = useState<MeetingCandidate | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [rejecting, setRejecting] = useState<MeetingCandidate | null>(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [corrected, setCorrected] = useState("");
  const [owner, setOwner] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [noDueDate, setNoDueDate] = useState(false);
  const [members, setMembers] = useState<{ id: string; displayName: string }[]>([]);
  useEffect(() => { void api.members(orgId).then((response) => setMembers(response.members)).catch(() => undefined); }, [orgId]);

  async function confirm() {
    if (!selected) return;
    setBusy(true);
    setError(null);
    try {
      if (selected.kind === "decision") {
        await meetingApi.confirmDecision(orgId, meetingId, { candidateId: selected.id, ...(corrected ? { correctedStatement: corrected } : {}) });
      } else {
        if (!owner || (!dueDate && !noDueDate)) {
          setError("Choose an organization member and either a due date or explicit no-due-date declaration.");
          return;
        }
        await meetingApi.confirmCommitment(orgId, meetingId, { candidateId: selected.id, ownerUserId: owner, dueDate: noDueDate ? null : dueDate, dueDateDeclaredAbsent: noDueDate });
      }
      setSelected(null); setCorrected(""); setOwner(""); setDueDate(""); setNoDueDate(false);
      onChanged();
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : "The governed confirmation could not be recorded.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      {(extractionOutcome === "FAILED_CLOSED" || extractionOutcome === "EMPTY") && (
        <StudioState
          state="ai_cannot_answer_safely"
          title="Meeting AI cannot answer safely"
          description={extractionOutcome === "FAILED_CLOSED"
            ? "Extraction could not produce a grounded answer safely."
            : "No grounded candidates were found; this is not a successful empty extraction."}
        />
      )}
      <Card>
        <CardHeader title="Candidate review" />
        <CardBody>
          {candidates.length === 0 && <EmptyState title="No candidates" description="Extraction has not produced a reviewable proposal." />}
          {candidates.map((candidate) => (
            <article key={candidate.id} style={{ borderBottom: "1px solid var(--ods-color-border)", padding: "var(--ods-space-3) 0" }}>
              <AiGeneratedFlag>AI generated · derived proposal</AiGeneratedFlag>
              <h3>{candidate.statement}</h3>
              <p>{candidate.basis}</p>
              <ConfidenceBadge value={candidate.confidence} />
              <CitationChip locator={`Documents source ${candidate.anchorSourceId}:${candidate.anchorStartOffset}-${candidate.anchorEndOffset}`} />
              <div className="ods-row">
                <Badge tone="warning">{meetingLabel(candidate.disposition)}</Badge>
                {candidate.disposition === "PENDING" && (
                  <>
                    {(candidate.kind === "decision" || candidate.kind === "commitment") && <Button size="sm" onClick={() => setSelected(candidate)}>Review proposal</Button>}
                    <Button size="sm" variant="secondary" onClick={() => setRejecting(candidate)}>Reject</Button>
                  </>
                )}
                {candidate.disposition === "PENDING" && candidate.kind !== "decision" && candidate.kind !== "commitment" && (
                  <span>Requires commitment context before confirmation.</span>
                )}
              </div>
            </article>
          ))}
        </CardBody>
      </Card>
      <ConfirmDialog
        open={selected !== null}
        title="Confirm derived proposal?"
        description="This governed action records the proposal as an asserted fact. Commitment confirmation additionally requires an owner and due-date declaration."
        confirmLabel="Confirm and record"
        busy={busy}
        error={error instanceof ApiError ? error.message : undefined}
        onConfirm={() => void confirm()}
        onCancel={() => { setSelected(null); setError(null); }}
      >
        {selected?.kind === "decision" && (
          <Card>
            <CardHeader title="Corrected decision wording" />
            <CardBody><Field label="Corrected wording">{(id) => <TextArea id={id} value={corrected} onChange={(event) => setCorrected(event.target.value)} />}</Field></CardBody>
          </Card>
        )}
        {selected?.kind === "commitment" && (
          <Card>
            <CardHeader title="Commitment governance inputs" />
            <CardBody>
              <Field label="Organization member owner">{(id) => <select id={id} value={owner} onChange={(event) => setOwner(event.target.value)}><option value="">Choose an owner</option>{members.map((member) => <option key={member.id} value={member.id}>{member.displayName}</option>)}</select>}</Field>
              <Field label="Due date">{(id) => <TextInput id={id} type="date" disabled={noDueDate} value={dueDate} onChange={(event) => setDueDate(event.target.value)} />}</Field>
              <label><input type="checkbox" checked={noDueDate} onChange={(event) => setNoDueDate(event.target.checked)} /> Explicitly no due date</label>
              <p>Commitment confirmation is blocked until both governance declarations are complete.</p>
            </CardBody>
          </Card>
        )}
      </ConfirmDialog>
      <ConfirmDialog
        open={rejecting !== null}
        title="Reject candidate"
        description="Provide the reason this derived proposal must not become a governed fact."
        confirmLabel="Reject candidate"
        onCancel={() => { setRejecting(null); setRejectionReason(""); }}
        onConfirm={() => {
          if (!rejecting || !rejectionReason.trim()) return;
          void meetingApi.rejectCandidate(orgId, meetingId, rejecting.id, rejectionReason.trim())
            .then(() => { setRejecting(null); setRejectionReason(""); onChanged(); })
            .catch((cause) => setError(cause));
        }}
      >
        <Field label="Rejection reason">{(id) => <TextArea id={id} value={rejectionReason} onChange={(event) => setRejectionReason(event.target.value)} />}</Field>
      </ConfirmDialog>
      {error !== null && <Refusal error={error} />}
    </>
  );
}
